/*
 * tt-policy-swap.js — moving between policies swaps the prose, not the page.
 *
 * Josh, 2026-08-20: "I think it's odd that changing Policy Pages refreshes the
 * top block with the lazy load soft animation. I don't think the top block
 * should 'refresh' at all. It should be a much smoother transition to the
 * various pages when clicking a different policy link in that top block."
 *
 * He is right, and the reveal is not really the fault - a full navigation tears
 * the whole document down and builds it again, so the masthead, the nav row and
 * the rail all arrive as if they had never been there. tt-reveal.js is only what
 * makes that visible. Suppressing the animation would hide the symptom and leave
 * the page still blinking through white between two documents that are 95% the
 * same.
 *
 * So the chrome STAYS. A click on the policy nav fetches the target, lifts four
 * things out of it - the title, the lede, the nav row and the body - cross-fades
 * them into the page that is already standing, and pushes the URL. Nothing above
 * the prose is rebuilt, so nothing above the prose can re-animate.
 *
 * WHY NOT ?section_id=. Two of these routes are not sections. `/policies/*` is
 * Shopify's own markup dropped into content_for_layout and wrapped by
 * layout/theme.liquid; there is no section to ask for. Fetching the page and
 * reading the DOM is the one method that works for both routes, which is the
 * same reason the chrome is a snippet pair rather than a section.
 *
 * EVERY FAILURE FALLS BACK TO A NORMAL NAVIGATION. No fetch, no DOMParser, no
 * pushState, a non-200, a document missing the hooks, a modified click, a
 * cross-origin href - all of them let the browser do what it was going to do.
 * The worst case is the behaviour we have today, never a dead link.
 */
(function () {
  'use strict';

  if (!window.fetch || !window.DOMParser || !history.pushState) return;

  var BODY = '[data-policy-body]';
  var NAV = '.tt-policy__nav';
  var TITLE = '.tt-policy__title';
  var LEDE = '.tt-policy__lede';
  var HEAD = '.tt-policy__head';

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  var OUT = 170;
  var IN = 280;

  function q(sel, root) { return (root || document).querySelector(sel); }

  /* The whole feature is off unless the page really is a policy. Both routes
     land here - the wrapper is shared - but a template that only half-matches
     must not be half-swapped. */
  function ready() {
    return q(BODY) && q(NAV) && q(TITLE) && q(HEAD);
  }

  var busy = false;

  /* --------------------------------------------------------------- the swap */

  function apply(doc, url, push) {
    var body = q(BODY);
    var nav = q(NAV);
    var title = q(TITLE);
    var head = q(HEAD);

    var newBody = q(BODY, doc);
    var newNav = q(NAV, doc);
    var newTitle = q(TITLE, doc);
    var newLede = q(LEDE, doc);
    if (!newBody || !newNav || !newTitle) return false;

    var lede = q(LEDE);
    var fading = [body, title];
    if (lede) fading.push(lede);

    var swap = function () {
      body.innerHTML = newBody.innerHTML;
      title.textContent = newTitle.textContent;

      /* The lede is optional and differs per policy, so it can appear, vanish or
         change. Reusing the node when both sides have one keeps the fade on a
         single element rather than removing one mid-transition. */
      if (lede && newLede) {
        lede.textContent = newLede.textContent;
      } else if (lede && !newLede) {
        lede.remove();
      } else if (!lede && newLede) {
        var mast = q('.tt-policy__masthead', document) || head;
        mast.appendChild(newLede.cloneNode(true));
      }

      /* The nav row is taken wholesale rather than hand-patched: which entries
         exist at all depends on which policies have a body, and the server is
         the only thing that knows. It carries no fade - the items do not move,
         only which one is the current page. */
      nav.innerHTML = newNav.innerHTML;

      if (doc.title) document.title = doc.title;
      if (push) history.pushState({ ttPolicy: true }, '', url);

      /* The rail is built from the headings in the body, so it has to be rebuilt
         from the body that is now there. */
      document.dispatchEvent(new CustomEvent('tt:policy:body-changed'));

      /* A screen reader gets no navigation event out of this, so move focus the
         way a real page load would - to the heading that just changed. */
      if (!title.hasAttribute('tabindex')) title.setAttribute('tabindex', '-1');
      try { title.focus({ preventScroll: true }); } catch (e) { title.focus(); }

      if (window.scrollY > 0) {
        window.scrollTo({ top: 0, behavior: reduced && reduced.matches ? 'auto' : 'smooth' });
      }
    };

    if (reduced && reduced.matches) {
      swap();
      busy = false;
      return true;
    }

    for (var i = 0; i < fading.length; i++) fading[i].classList.add('is-swapping');

    window.setTimeout(function () {
      swap();
      /* Two frames again. The nodes have to be painted in their faded state with
         their NEW contents before the class comes off, or the browser coalesces
         the two changes and the prose appears rather than arriving - the same
         trick tt-reveal.js and tt-shoplook.js both need. */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          for (var j = 0; j < fading.length; j++) fading[j].classList.remove('is-swapping');
          window.setTimeout(function () { busy = false; }, IN);
        });
      });
    }, OUT);

    return true;
  }

  function go(url, push) {
    if (busy) return;
    busy = true;
    fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'fetch' } })
      .then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        if (!apply(doc, url, push)) throw new Error('hooks missing');
      })
      .catch(function () {
        busy = false;
        window.location.href = url;
      });
  }

  /* --------------------------------------------------------------- the click */

  document.addEventListener('click', function (event) {
    if (!ready()) return;
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var a = event.target.closest && event.target.closest(NAV + ' a[href]');
    if (!a) return;
    if (a.target && a.target !== '_self') return;

    var url;
    try { url = new URL(a.href, window.location.href); } catch (e) { return; }
    if (url.origin !== window.location.origin) return;
    if (url.pathname === window.location.pathname) { event.preventDefault(); return; }

    event.preventDefault();
    go(url.pathname + url.search, true);
  });

  /* Back and forward have to undo it, or the URL and the prose disagree. Only
     for entries this pushed: a state without the marker belongs to whatever
     page came before the policy, and that one is a real navigation. */
  window.addEventListener('popstate', function (event) {
    if (!ready()) return;
    if (!event.state || !event.state.ttPolicy) {
      window.location.reload();
      return;
    }
    go(window.location.pathname + window.location.search, false);
  });

  /* The entry the visitor arrived on carries no state, so mark it - otherwise
     going back to the first policy of a session reloads instead of swapping. */
  if (ready() && (!history.state || !history.state.ttPolicy)) {
    history.replaceState({ ttPolicy: true }, '', window.location.href);
  }
})();
